import { inject, Injectable } from '@angular/core';
import { FeatureFlagService } from '@ddv/entitlements';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { AppWidgetState, LegacyWidgetSettings, NamedQuery, Sharing, UserPermission, WidgetSettings } from '@ddv/models';
import { NamedQueriesService } from '@ddv/named-queries';
import { map, Observable, of, Subject, switchMap } from 'rxjs';

import { LegacyWidgetOnBoard } from '../models/legacy-widget-on-board';
import { NewWidgetOnBoard } from '../models/new-widget-on-board';
import { WidgetOnBoard } from '../models/widget-on-board';

@Injectable()
export class WidgetOnBoardService {
    private readonly ddvApiService = inject<ApiExecutorService>(ApiServices.ddvApi);
    private readonly ddvMwService = inject<ApiExecutorService>(ApiServices.ddvMW);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly namedQueriesService = inject(NamedQueriesService);

    public readonly widgetAddedToBoard: Observable<void>;
    public readonly widgetRemovedFromBoard: Observable<void>;

    private readonly widgetAddedToBoardSubject = new Subject<void>();
    private readonly widgetRemovedFromBoardSubject = new Subject<void>();

    constructor() {
        this.widgetAddedToBoard = this.widgetAddedToBoardSubject.asObservable();
        this.widgetRemovedFromBoard = this.widgetRemovedFromBoardSubject.asObservable();
    }

    getWidgetsOnBoard(clientCode: string, dashboardId: string | number): Observable<WidgetOnBoard[]> {
        return this.ddvMwService.invokeService<Partial<LegacyWidgetOnBoard>[]>(clientCode, `dashboard/${dashboardId}/widgets`, 'GET')
            .pipe(
                map((jsons) => {
                    // Mapping widget settings and sharing from the legacy format to the new format
                    const wobs = jsons.map((json) => getUpdatedWidgetOnBoard(json));
                    return wobs.map((json) => new WidgetOnBoard(json));
                }),
            );
    }

    addWidgetToBoard(clientCode: string, dashboardId: string | number, widgetId: number): Observable<WidgetOnBoard> {
        return this.featureFlagService.isFlagEnabled('use-aws-for-widgets', clientCode)
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return this.addWidgetToBoardUsingApi(clientCode, dashboardId, widgetId);
                    }

                    return this.addWidgetToBoardUsingMw(clientCode, dashboardId, widgetId);
                }),
            );
    }

    removeWidgetFromBoard(clientCode: string, dashboardId: string | number, widgetOnBoardId: number): Observable<void> {
        return this.featureFlagService.isFlagEnabled('use-aws-for-widgets', clientCode)
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return this.ddvApiService
                            .invokeServiceWithBody<void>(clientCode, `dashboards/${dashboardId}/widgets/${widgetOnBoardId}`, 'DELETE', null);
                    }
                    return this.ddvMwService.invokeService<void>(clientCode, `dashboards/${dashboardId}/widgets/${widgetOnBoardId}`, 'DELETE');
                }),
            );
    }

    // This method and the one below it are totally stupid.
    // They are only called by the thing that calls addWidgetToBoard or removeWidgetFromBoard,
    // but those callers do all kinds of crazy things with lifecycle events and the manager service
    // so it's currently impossible to just emit the event from the add/remove methods above
    notifyForWidgetAddedOnDashboard(): void {
        this.widgetAddedToBoardSubject.next();
    }

    notifyForWidgetRemovedFromDashboard(): void {
        this.widgetRemovedFromBoardSubject.next();
    }

    addDetailWidgetToBoard(clientCode: string, widgetId: number): Observable<AppWidgetState> {
        return this.featureFlagService.isFlagEnabled('use-aws-for-widgets', clientCode)
            .pipe(
                switchMap((isFlagEnabled) => {
                    if (isFlagEnabled) {
                        return this.ddvApiService.invokeServiceWithParams<Partial<AppWidgetState>>(clientCode, `widgets/${widgetId}`);
                    }
                    return this.ddvMwService.invokeServiceWithParams<Partial<AppWidgetState>>(clientCode, `widgets/${widgetId}`);
                }),
                map((widget) => new AppWidgetState(widget)),
            );
    }

    private addWidgetToBoardUsingApi(clientCode: string, dashboardId: string | number, widgetId: number): Observable<WidgetOnBoard> {
        // Remove the query param isUICall after the migration is complete
        return this.ddvApiService.invokeService<Partial<NewWidgetOnBoard>>(clientCode, `dashboards/${dashboardId}/widgets/${widgetId}?isUICall=true`, 'POST')
            .pipe(
                switchMap((json: Partial<NewWidgetOnBoard>) => {
                    // After the migration it would be impossible for the API to return a widget without named query.
                    // We should fix the type and remove this case.
                    if (!json.widget?.namedQueryId) {
                        return of(json);
                    }

                    return this.namedQueriesService.fetchNamedQuery(json.widget.namedQueryId)
                        .pipe(
                            map((namedQuery: NamedQuery | undefined) => {
                                if (json.widget) {
                                    json.widget.namedQuery = namedQuery;
                                }
                                return json;
                            }),
                        );
                }),
                map((json: Partial<NewWidgetOnBoard>) => {
                    const wob = fromNewPartialWidgetOnBoard(json as NewWidgetOnBoard);
                    return new WidgetOnBoard(wob);
                }),
            );
    }

    private addWidgetToBoardUsingMw(clientCode: string, dashboardId: string | number, widgetId: number): Observable<WidgetOnBoard> {
        return this.ddvMwService.invokeService<Partial<LegacyWidgetOnBoard>>(clientCode, `dashboards/${dashboardId}/widgets/${widgetId}`, 'POST')
            .pipe(
                switchMap((json: Partial<LegacyWidgetOnBoard>) => {
                    if (!json.widget?.namedQueryId) {
                        return of(json);
                    }

                    return this.namedQueriesService.fetchNamedQuery(json.widget.namedQueryId)
                        .pipe(
                            map((namedQuery: NamedQuery | undefined) => {
                                if (json.widget) {
                                    json.widget.namedQuery = namedQuery;
                                }
                                return json;
                            }),
                        );
                }),
                map((json: Partial<LegacyWidgetOnBoard>) => {
                    // Mapping widget settings and sharing from the legacy format to the new format
                    const wob = getUpdatedWidgetOnBoard(json);
                    return new WidgetOnBoard(wob);
                }),
            );
    }
}

// This should be removed after the migration, when we switch to the new type of widget on board on the UI
export function fromNewPartialWidgetOnBoard(newWob: NewWidgetOnBoard): WidgetOnBoard {
    return new WidgetOnBoard({
        id: newWob.id,
        height: newWob.height,
        width: newWob.width,
        maximized: newWob.isMaximized,
        top: newWob.topPosition,
        left: newWob.leftPosition,
        widgetPosition: {
            startingRow: newWob.startRow,
            endRow: newWob.endRow,
            startingColumn: newWob.startColumn,
            endColumn: newWob.endColumn,
            minWidth: 200,
        },
        widget: newWob.widget,
    });
}

function getUpdatedWidgetOnBoard(wob: Partial<LegacyWidgetOnBoard>): WidgetOnBoard {
    if (!wob.widget) {
        return new WidgetOnBoard({ ...wob, widget: undefined });
    }

    const { widgetPermissions, widgetSettings, ...rest } = wob.widget;

    return new WidgetOnBoard({
        ...wob,
        widget: new AppWidgetState({
            ...rest,
            widgetSettings: getWidgetSettings(wob.widget.widgetSettings),
            sharing: fromPermissions(wob.widget.widgetPermissions ?? []),
        }),
    });
}

function getWidgetSettings(legacyWidgetSettings: LegacyWidgetSettings[] | undefined): WidgetSettings {
    return {
        isSubscribedToDashboardFilters: !!legacyWidgetSettings?.[0]?.isSubscribedToDashboardFilters,
        isActiveDate: !!legacyWidgetSettings?.[0]?.isActiveDate,
    };
}

// This converts the permissions coming from the MW to the new sharing type
function fromPermissions(permissions: UserPermission[]): Sharing {
    if (!permissions) {
        return { toUsers: [], toTeams: [] };
    }

    return {
        toUsers: permissions
            .filter((p) => p.sharedType === 'USER')
            .map((p) => ({ name: p.name, permission: p.privilege === 'EDIT' ? 'write' : 'read' })),
        toTeams: permissions
            .filter((p) => p.sharedType === 'TEAM')
            .map((p) => ({ name: p.name, permission: p.privilege === 'EDIT' ? 'write' : 'read' })),
    };
}
